import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Master, MastersSearchConditions } from '../../../api/model';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import PageNavi from '../../modules/PageNavi';
import { MasterListDownload } from './MasterListDownload';

type Props = {
  masters: Master[];
  conditions: MastersSearchConditions;
};
const N_DATA_PER_PAGE = 50;

// 一覧のソートキー
type SortKey = 'master_id' | 'master_name' | 'version_number' | 'update_at' | 'user_name' | 'memo';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

function MasterList({ masters, conditions }: Props) {
  const { t, i18n } = useTranslation();

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Masters.CurrentPage', 1));

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Masters.SortInfo', { key: 'master_id' as SortKey, isDisc: false })
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 一覧データをソートしたもの
  const sortedMasters = useMemo(
    () =>
      [...masters].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        return 1;
      }),
    [sortInfo, masters]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('Masters.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Masters.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (masters != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [masters]);

  // 現在のページに表示する一覧
  const filteredMasters = useMemo(
    () => sortedMasters.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, sortedMasters]
  );

  return (
    <div style={{ marginTop: '30px' }}>
      <h3 className="">
        <span>{t('マスタ一覧')}</span>
      </h3>

      <div className="wfr in-pgnavi">
        <div className="outputfr mb15">
          <MasterListDownload conditions={conditions} />
        </div>
        <table className="table38 mb30">
          <tbody>
            <tr>
              {/* <th>
                <span className="ck-box">
                  <input type="checkbox" id="ck57-1" name="" /> <label htmlFor="ck57-1">&nbsp;</label>
                </span>
              </th> */}
              <th> </th>
              <SortableTh label={t('マスタ/テンプレート名')} onClick={() => changeSortKey('master_name')} />
              <SortableTh label={t('バージョン')} onClick={() => changeSortKey('version_number')} />
              <SortableTh label={t('更新日時')} onClick={() => changeSortKey('update_at')} />
              <SortableTh label={t('更新ユーザー')} onClick={() => changeSortKey('user_name')} />
              <SortableTh label={t('メモ')} onClick={() => changeSortKey('memo')} />
            </tr>
            {filteredMasters.map((master) => (
              <tr key={master.master_version_id}>
                {/* <td>
                  <span className="ck-box">
                    <input type="checkbox" id="ck57-2" name="" /> <label htmlFor="ck57-2">&nbsp;</label>
                  </span>
                </td> */}
                <td />
                <td>
                  {master.master_kbn === 2 && (
                    <Link to={`/${i18n.language}/masterslist/schedule/${master.master_version_id?.toString() ?? ''}`}>
                      {master.master_name}
                    </Link>
                  )}
                  {master.master_kbn !== 2 && (
                    <Link to={`/${i18n.language}/masterslist/edit/${master.master_version_id?.toString() ?? ''}`}>
                      {master.master_name}
                    </Link>
                  )}
                </td>
                <td>{master.version_number}</td>
                <td>{master.update_at}</td>
                <td>{master.user_name}</td>
                <td>{master.memo}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PageNavi
          current={currentPage}
          pageCount={Math.ceil(masters.length / (N_DATA_PER_PAGE * 1.0))}
          onChangePage={changeCurrentPage}
        />
      </div>
    </div>
  );
}

export { MasterList };
